import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { SITE } from "@/lib/site";

export function LegalPage({ title, children }) {
  return (
    <div className="min-h-screen bg-white">
      {/* Simple header */}
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="legal-back-home">
            <div className="w-11 h-11 rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 flex items-center justify-center p-1.5 group-hover:scale-105 transition-transform">
              <img src={SITE.logo} alt="Scharpf Logo" className="w-full h-full object-contain" />
            </div>
            <div className="leading-tight">
              <div className="font-heading text-navy text-lg font-extrabold tracking-tight">Scharpf</div>
              <div className="text-[11px] text-slate-500 uppercase tracking-[0.18em]">
                Haushaltsauflösungen
              </div>
            </div>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-navy hover:bg-slate-100 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Zur Startseite
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 md:px-10 py-14 md:py-20">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-green-accent" style={{ color: "#3d8a2c" }}>
          Rechtliches
        </div>
        <h1 className="font-heading mt-3 text-4xl md:text-5xl font-black text-navy tracking-tight">
          {title}
        </h1>
        <div className="mt-10 space-y-5 text-slate-700 leading-relaxed text-base md:text-lg [&_h2]:font-heading [&_h2]:text-2xl [&_h2]:md:text-3xl [&_h2]:font-extrabold [&_h2]:text-navy [&_h2]:mt-10 [&_h2]:mb-2 [&_h3]:font-heading [&_h3]:text-lg [&_h3]:font-bold [&_h3]:text-navy [&_h3]:mt-6 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1 [&_a]:text-navy [&_a]:underline hover:[&_a]:text-green-accent">
          {children}
        </div>
      </main>

      <footer className="border-t border-slate-200 mt-12">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-8 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-500">
          <div>© {new Date().getFullYear()} Scharpf Haushaltsauflösungen & Entrümpelungen</div>
          <div className="flex gap-5">
            <Link to="/impressum" className="hover:text-navy">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-navy">Datenschutz</Link>
            <Link to="/agb" className="hover:text-navy">AGB</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export function Impressum() {
  return (
    <LegalPage title="Impressum">
      <h2>Angaben gemäß § 5 TMG</h2>
      <p>
        <strong>Scharpf Haushaltsauflösungen & Entrümpelungen</strong>
        <br />
        Inhaber: Kevin Scharpf
        <br />
        Oskarstraße 65
        <br />
        46145 Oberhausen
        <br />
        Deutschland
      </p>

      <h3>Kontakt</h3>
      <p>
        Telefon: <a href={`tel:${SITE.phoneE164}`}>+49 172 6768531</a>
        <br />
        E-Mail: <a href={`mailto:${SITE.email}`}>{SITE.email}</a>
      </p>

      <h2>Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV</h2>
      <p>
        Kevin Scharpf
        <br />
        Oskarstraße 65
        <br />
        46145 Oberhausen
      </p>

      <h2>Umsatzsteuer</h2>
      <p>
        Gemäß § 19 UStG wird keine Umsatzsteuer berechnet (Kleinunternehmerregelung).
      </p>
    </LegalPage>
  );
}

export function Datenschutz() {
  return (
    <LegalPage title="Datenschutzerklärung">
      <p>
        Der Schutz Ihrer personenbezogenen Daten ist uns ein wichtiges Anliegen. Wir behandeln Ihre
        Daten vertraulich und entsprechend den gesetzlichen Datenschutzvorschriften (insbesondere
        der DSGVO) sowie dieser Datenschutzerklärung.
      </p>

      <h2>1. Verantwortlicher</h2>
      <p>
        Kevin Scharpf
        <br />
        Scharpf Haushaltsauflösungen & Entrümpelungen
        <br />
        Oskarstraße 65
        <br />
        46145 Oberhausen
        <br />
        Telefon: <a href={`tel:${SITE.phoneE164}`}>+49 172 6768531</a>
        <br />
        E-Mail: <a href={`mailto:${SITE.email}`}>{SITE.email}</a>
      </p>

      <h2>2. Erhebung personenbezogener Daten bei Kontaktaufnahme</h2>
      <p>
        Wenn Sie uns per Kontaktformular, E-Mail oder Telefon kontaktieren, verarbeiten wir die von
        Ihnen übermittelten Daten ausschließlich zur Bearbeitung Ihrer Anfrage. Dies können sein:
        Name, Telefonnummer, E-Mail-Adresse, Anschrift, Informationen zur gewünschten Dienstleistung.
      </p>
      <p>
        Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO (vorvertragliche bzw. vertragliche Maßnahmen),
        hilfsweise Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an der Bearbeitung Ihrer
        Anfrage). Die Angabe dieser Daten ist freiwillig, aber für die Bearbeitung Ihrer Anfrage
        erforderlich.
      </p>
      <p>
        Wir speichern diese Daten, bis Ihre Anfrage abschließend bearbeitet ist, und löschen sie
        anschließend, sofern keine gesetzlichen Aufbewahrungspflichten (z. B. handels- oder
        steuerrechtlich) entgegenstehen.
      </p>

      <h2>3. Kontaktformular</h2>
      <p>
        Die über das Kontaktformular übermittelten Daten werden ausschließlich zur Bearbeitung Ihrer
        Anfrage gespeichert und nicht ohne Ihre Einwilligung an Dritte weitergegeben.
      </p>

      <h2>4. E-Mail-Kommunikation</h2>
      <p>
        Unsere geschäftliche E-Mail-Adresse wird über Gmail (Google Ireland Limited bzw. Google LLC)
        betrieben. Dadurch können beim Versand und Empfang von E-Mails Daten auf Servern von Google
        verarbeitet werden, auch außerhalb der EU (USA). Google verarbeitet diese Daten nach eigenen
        Angaben auf Grundlage von Standardvertragsklauseln. Rechtsgrundlage für uns ist Art. 6 Abs.
        1 lit. f DSGVO (berechtigtes Interesse an funktionierender Geschäftskommunikation). Weitere
        Informationen: Datenschutzerklärung von Google.
      </p>

      <h2>5. WhatsApp</h2>
      <p>
        Wenn Sie uns über WhatsApp kontaktieren, erfolgt die Kommunikation über WhatsApp Ireland
        Limited, das zum Meta-Konzern gehört. Dabei können personenbezogene Daten (z. B.
        Telefonnummer, Kommunikationsinhalte, Metadaten) verarbeitet und auch in die USA bzw. an
        andere Meta-Gesellschaften übermittelt werden. Rechtsgrundlage ist Art. 6 Abs. 1 lit. a
        DSGVO, da Sie durch die Kontaktaufnahme über WhatsApp in diese Verarbeitung einwilligen.
        Wenn Sie dies nicht wünschen, kontaktieren Sie uns bitte per Telefon oder E-Mail. Weitere
        Informationen: Datenschutzrichtlinie von WhatsApp/Meta.
      </p>

      <h2>6. Hosting</h2>
      <p>
        Diese Website wird von IONOS gehostet. Der Hoster verarbeitet in unserem Auftrag u. a.
        Server-Logfiles. Mit dem Hoster besteht ein Auftragsverarbeitungsvertrag nach Art. 28
        DSGVO. Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an sicherer
        und stabiler Bereitstellung der Website).
      </p>

      <h2>7. Server-Logfiles</h2>
      <p>
        Beim Besuch unserer Website werden automatisch Informationen erhoben, beispielsweise:
        IP-Adresse, Browsertyp, Betriebssystem, Datum und Uhrzeit, besuchte Seiten. Diese Daten
        dienen ausschließlich der technischen Bereitstellung und Sicherheit der Website (Art. 6
        Abs. 1 lit. f DSGVO) und werden nach spätestens 7 Tagen automatisch gelöscht.
      </p>

      <h2>8. Cookies</h2>
      <p>
        Diese Website verwendet nur technisch notwendige Cookies, die für den Betrieb der Seite
        erforderlich sind. Eine Einwilligung ist hierfür nicht erforderlich (§ 25 Abs. 2 TDDDG).
        Sofern zusätzlich Analyse- oder Marketing-Tools eingesetzt werden, holen wir vorab Ihre
        Einwilligung über ein Consent-Tool ein und ergänzen diese Erklärung entsprechend.
      </p>

      <h2>9. Keine automatisierte Entscheidungsfindung</h2>
      <p>Eine automatisierte Entscheidungsfindung einschließlich Profiling findet nicht statt.</p>

      <h2>10. Ihre Rechte</h2>
      <p>
        Sie haben jederzeit das Recht auf Auskunft (Art. 15 DSGVO), Berichtigung (Art. 16 DSGVO),
        Löschung (Art. 17 DSGVO), Einschränkung der Verarbeitung (Art. 18 DSGVO),
        Datenübertragbarkeit (Art. 20 DSGVO) und Widerspruch gegen die Verarbeitung (Art. 21
        DSGVO). Erteilte Einwilligungen können Sie jederzeit mit Wirkung für die Zukunft widerrufen
        (Art. 7 Abs. 3 DSGVO).
      </p>

      <h2>11. Beschwerderecht bei einer Aufsichtsbehörde</h2>
      <p>
        Sie haben das Recht, sich bei einer Datenschutzaufsichtsbehörde zu beschweren. Zuständig
        ist in der Regel:
      </p>
      <p>
        Landesbeauftragte für Datenschutz und Informationsfreiheit Nordrhein-Westfalen (LDI NRW)
        <br />
        Kavalleriestraße 2–4
        <br />
        40213 Düsseldorf
      </p>

      <h2>12. SSL-Verschlüsselung</h2>
      <p>Unsere Website nutzt eine SSL-Verschlüsselung zum Schutz Ihrer Daten bei der Übertragung.</p>

      <p className="text-sm text-slate-500 mt-8">Stand: Juli 2026</p>
    </LegalPage>
  );
}

export function AGB() {
  return (
    <LegalPage title="Allgemeine Geschäftsbedingungen (AGB)">
      <h2>1. Geltungsbereich</h2>
      <p>
        Diese AGB gelten für alle Verträge zwischen Scharpf Haushaltsauflösungen & Entrümpelungen
        und dem Auftraggeber.
      </p>

      <h2>2. Leistungen</h2>
      <p>Wir bieten insbesondere folgende Dienstleistungen an:</p>
      <ul>
        <li>Haushaltsauflösungen</li>
        <li>Entrümpelungen</li>
        <li>Wohnungsauflösungen</li>
        <li>Keller- und Dachbodenräumungen</li>
        <li>Garagenräumungen</li>
        <li>Messie-Wohnungen</li>
        <li>Nachlassauflösungen</li>
        <li>Nachlassankauf</li>
        <li>Goldankauf</li>
        <li>Ankauf von Schmuck und Edelmetallen</li>
      </ul>

      <h2>3. Angebot</h2>
      <p>
        Unsere Angebote sind unverbindlich. Erst mit der Auftragsbestätigung kommt ein Vertrag
        zustande.
      </p>

      <h2>4. Preise</h2>
      <p>
        Alle Preise verstehen sich gemäß der Kleinunternehmerregelung nach § 19 UStG ohne Ausweis
        der Umsatzsteuer.
      </p>

      <h2>5. Zahlung</h2>
      <p>
        Die Zahlung erfolgt unmittelbar nach Abschluss der Arbeiten oder entsprechend der
        individuellen Vereinbarung.
      </p>

      <h2>6. Termine</h2>
      <p>
        Vereinbarte Termine werden nach Möglichkeit eingehalten. Verzögerungen aufgrund höherer
        Gewalt oder unvorhersehbarer Ereignisse berechtigen nicht zu Schadensersatzansprüchen.
      </p>

      <h2>7. Haftung</h2>
      <p>
        Wir haften nur für Schäden, die vorsätzlich oder grob fahrlässig verursacht wurden, soweit
        gesetzlich zulässig.
      </p>

      <h2>8. Schlussbestimmungen</h2>
      <p>Es gilt deutsches Recht.</p>
    </LegalPage>
  );
}
